import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import Image from 'next/image';
import Link from 'next/link';

interface DynamicModalProps {
  show: boolean;
  onHide: () => void;
  title?: string;
  description?: string;
  imageUrl?: string;
  buttonText?: string;
  buttonLink?: string;
  size?: 'sm' | 'lg' | 'xl';
  centered?: boolean;
  imageOnly?: boolean;
}

const DynamicModal: React.FC<DynamicModalProps> = ({
  show,
  onHide,
  title,
  description,
  imageUrl,
  buttonText,
  buttonLink = '#',
  size = 'lg',
  centered = true,
  imageOnly = false,
}) => {
  const showCustomCloseButton = !title || imageOnly;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size={size}
      centered={centered}
      aria-labelledby={title ? 'dynamic-modal-title' : undefined}
      className={imageOnly ? 'image-only-modal' : ''}
    >
      {/* Custom close button when there's no title or in image-only mode */}
      {showCustomCloseButton && (
        <button
          type="button"
          className="btn-close position-absolute"
          style={{
            top: '15px',
            right: '15px',
            zIndex: 1060,
            backgroundColor: 'white',
            padding: '0.5rem',
            opacity: 0.85,
            border: 'none',
            borderRadius: '0.25rem',
          }}
          onClick={onHide}
          aria-label="Close"
        />
      )}

      {/* Standard header with close button when title exists */}
      {title && (
        <Modal.Header className="border-0" closeButton>
          <Modal.Title
            id="dynamic-modal-title fw-bold"
            className="w-100 text-center "
          >
            {title}
          </Modal.Title>
        </Modal.Header>
      )}

      <Modal.Body
        className={`d-flex flex-column align-items-center ${imageOnly ? 'p-0' : 'p-0'}`}
      >
        {imageUrl && (
          <div className={imageOnly ? 'w-100' : 'mb-4 w-100 text-center'}>
            <Image
              src={
                imageUrl?.startsWith('http') ? imageUrl : `https://${imageUrl}`
              }
              alt="Modal content"
              width={imageOnly ? 1920 : 800}
              height={imageOnly ? 1080 : 400}
              className={`img-fluid ${imageOnly ? 'w-100 h-auto' : 'rounded'}`}
              style={{
                maxHeight: imageOnly ? '100vh' : '50vh',
                objectFit: 'cover',
              }}
              priority
            />
          </div>
        )}

        {!imageOnly && (
          <>
            {description && (
              <p className={`text-center ${buttonText ? 'mb-2' : ''}`}>
                {description}
              </p>
            )}

            {buttonText && (
              <div className="d-flex align-items-center gap-4 mt-2 mb-4">
                <button className="trk-btn trk-btn--outline2" onClick={onHide}>
                  Cancel
                </button>
                <Link
                  className="trk-btn trk-btn--secondary"
                  href={buttonLink}
                  onClick={onHide}
                >
                  {buttonText}
                </Link>
              </div>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default DynamicModal;
